/**
 * 🔓 «پول فروشنده» — کنترل آزادسازی وجه یک فروشگاه.
 *
 * اصل طراحی: اپراتور این پنجره را وقتی باز می‌کند که فروشنده پولش را می‌خواهد.
 * پس بالای صفحه باید جوابِ همان سؤال باشد — «چقدر می‌تواند بگیرد، بقیه کِی
 * خودش آزاد می‌شود» — و فرم دخالت دستی فقط اگر خواست دستکاری کند.
 *
 *   بالا        : یک جمله + نوار ترکیب پول قفل‌شده + خط زمانِ آزادسازی خودکار
 *   تب «آزاد کردن پول» : دو سؤال (از کدام پول؟ چقدر؟) با اسلایدر و نتیجهٔ زندهٔ خودکار
 *   تب «مدت گارانتی»   : سه عدد + مثال تاریخ واقعی
 *   تب «سفارش‌ها»       : جست‌وجو/فیلتر + اقدام تک‌سطری + اقدام گروهی
 *
 * هیچ پولی جابه‌جا نمی‌شود؛ فقط «لحظهٔ آزادشدن» عوض می‌شود و موجودی فروشنده
 * بلافاصله همان را می‌بیند. هر اقدام در دفتر رویدادهای مالی فروشنده ثبت می‌شود.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, Slider, TextField, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import moment from 'moment-jalaali';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockClockRoundedIcon from '@mui/icons-material/LockClockRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoRoundedIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ResponsiveModal } from '../common';
import storeEscrowService, { BUCKET_META, EscrowBucket, EscrowItem, EscrowOverview, ReleasePlan } from '../../services/storeEscrowService';
import { useAccess } from '../../hooks/useAccess';
import '../../pages/FinanceAccounting.css';

const fa = (v: any) => String(v ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
const num = (n?: number | null) => (n == null || !isFinite(Number(n)) ? '—' : new Intl.NumberFormat('fa-IR').format(Math.round(Number(n))));
const toman = (n?: number | null) => (n == null || !isFinite(Number(n)) ? '—' : `${num(n)} تومان`);
/** عدد خوانا برای نگاه اول: «۴۶٫۵ میلیون» */
const compact = (n?: number | null) => {
  if (n == null || !isFinite(Number(n))) return '—';
  const v = Number(n); const a = Math.abs(v);
  if (a >= 1e9) return `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format(v / 1e9)} میلیارد`;
  if (a >= 1e6) return `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format(v / 1e6)} میلیون`;
  if (a >= 1e3) return `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 }).format(v / 1e3)} هزار`;
  return num(v);
};
const jd = (iso?: string | null, withTime = false) => {
  if (!iso) return '—';
  try { return fa(moment(iso).format(withTime ? 'jYYYY/jMM/jDD HH:mm' : 'jYYYY/jMM/jDD')); } catch { return '—'; }
};
const jdIn = (days: number) => fa(moment().add(days, 'day').format('jYYYY/jMM/jDD'));
const toEn = (s: string) => String(s || '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));

const KIND_FA: Record<string, string> = { digital: 'دیجیتال', postal_delivery: 'پستی', info_only: 'اطلاع‌رسانی' };

/** سه سطح ریسک — جای دو چک‌باکس گیج‌کننده */
type Scope = 'safe' | 'undelivered' | 'everything';
const SCOPES: { key: Scope; short: string; why: string }[] = [
  { key: 'safe', short: 'تحویل‌شده', why: 'فقط سفارش‌هایی که خریدار کالا را گرفته و تنها دورهٔ گارانتی تمام نشده. کم‌خطرترین حالت و پیشنهاد ما.' },
  { key: 'undelivered', short: '+ تحویل‌نشده', why: 'سفارش‌هایی که پولش را گرفته‌ایم ولی کالا دست خریدار نیست هم اضافه می‌شود. اگر لغو شود باید پول را از فروشنده پس بگیری.' },
  { key: 'everything', short: 'همه، حتی مسدود', why: 'اختلاف باز، پرداخت ناتمام و بازرسی ناتمام هم اضافه می‌شود. اگر داوری به نفع خریدار تمام شود، استرداد از جیب پلتفرم می‌رود.' },
];

/** «اگر دست نزنی، خودش کِی آزاد می‌شود» */
const WHEN_STEPS: { label: string; upTo: number }[] = [
  { label: 'امروز', upTo: 0 },
  { label: 'تا ۳ روز', upTo: 3 },
  { label: 'تا یک هفته', upTo: 7 },
  { label: 'تا یک ماه', upTo: 30 },
  { label: 'بعدتر', upTo: Infinity },
];

/** هر سه اقدامی که از پنجرهٔ تأیید رد می‌شوند */
type ConfirmAskShape =
  | { kind: 'release'; plan: ReleasePlan; input: any }
  | { kind: 'hold'; itemIds: string[]; amount: number; days: number }
  | { kind: 'clear'; itemIds: string[]; amount: number };

interface Props {
  storeId: string;
  storeName: string;
  open: boolean;
  onClose: () => void;
  /** بعد از هر تغییر، صفحهٔ فروشگاه‌ها هم تازه شود */
  onChanged?: () => void;
}

const StoreEscrowDialog: React.FC<Props> = ({ storeId, storeName, open, onClose, onChanged }) => {
  const { has } = useAccess();
  const canManage = has('admin_store_escrow_manage');
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState<'release' | 'policy' | 'items'>('release');
  const [data, setData] = useState<EscrowOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReleased, setShowReleased] = useState(false);
  const [busy, setBusy] = useState(false);

  // تب ۱
  const [scope, setScope] = useState<Scope>('safe');
  const [amount, setAmount] = useState(0);          // ۰ = همه‌اش
  const [plan, setPlan] = useState<ReleasePlan | null>(null);
  const [planning, setPlanning] = useState(false);

  // تب ۲
  const [pol, setPol] = useState({ digital: '', postal: '', physical: '', note: '' });
  const [savingPolicy, setSavingPolicy] = useState(false);

  // تب ۳
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [bucketFilter, setBucketFilter] = useState<EscrowBucket | 'all' | 'risky'>('all');
  const [holdDays, setHoldDays] = useState('7');

  const [confirm, setConfirm] = useState<ConfirmAskShape | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await storeEscrowService.overview(storeId, showReleased);
      setData(d);
      setPol({
        digital: d.policy.source.digital === 'store' ? String(d.policy.digitalDays) : '',
        postal: d.policy.source.postal === 'store' ? String(d.policy.postalDays) : '',
        physical: d.policy.source.physical === 'store' ? String(d.policy.physicalDays) : '',
        note: d.policy.note || '',
      });
    } catch (e: any) {
      toast.error(e?.message || 'خطا در دریافت وضعیت');
    } finally {
      setLoading(false);
    }
  }, [storeId, showReleased]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const items = useMemo(() => data?.items || [], [data]);
  const t = data?.totals;
  const bal = data?.balance;
  const locked = (t?.escrow || 0) + (t?.awaitingDelivery || 0) + (t?.blocked || 0);

  const ceiling = useMemo(() => ({
    safe: t?.escrow || 0,
    undelivered: (t?.escrow || 0) + (t?.awaitingDelivery || 0),
    everything: locked,
  }), [t, locked]);
  const cap = ceiling[scope];

  // اگر دستهٔ کم‌خطر خالی باشد ولی پول قفل‌شده وجود داشته باشد، خودمان روی اولین
  // دستهٔ دارای پول می‌رویم؛ وگرنه کاربر «صفر» می‌بیند و فکر می‌کند صفحه خراب است.
  const autoScoped = useRef(false);
  useEffect(() => {
    if (!open) { autoScoped.current = false; return; }
    if (autoScoped.current || !data) return;
    autoScoped.current = true;
    const tt: any = data.totals || {};
    if ((tt.escrow || 0) > 0) setScope('safe');
    else if ((tt.awaitingDelivery || 0) > 0) setScope('undelivered');
    else if ((tt.blocked || 0) > 0) setScope('everything');
  }, [open, data]);

  // تغییر دسته ⇒ سقف عوض می‌شود، مبلغ انتخابی را داخل سقف نگه داریم
  useEffect(() => { setAmount((a) => (a > cap ? cap : a)); }, [cap]);

  /** «اگر دست نزنی، خودش کِی آزاد می‌شود» — فقط اقلام در گارانتی تاریخ دارند */
  const when = useMemo(() => {
    const sums = WHEN_STEPS.map(() => 0);
    items.forEach((i) => {
      if (i.bucket !== 'escrow' || !i.releaseDate) return;
      const d = Math.max(0, i.daysLeft ?? 0);
      const ix = WHEN_STEPS.findIndex((s) => d <= s.upTo);
      sums[ix < 0 ? WHEN_STEPS.length - 1 : ix] += i.amount;
    });
    return WHEN_STEPS.map((s, ix) => ({ ...s, amount: sums[ix] })).filter((s) => s.amount > 0);
  }, [items]);

  const releaseInput = useCallback(() => ({
    mode: amount > 0 && amount < cap ? ('amount' as const) : ('all' as const),
    amount: amount > 0 && amount < cap ? amount : undefined,
    includeAwaitingDelivery: scope !== 'safe',
    includeBlocked: scope === 'everything',
  }), [amount, cap, scope]);

  // ── نتیجه خودکار حساب می‌شود؛ کاربر دکمهٔ «پیش‌نمایش» اضافه‌ای نمی‌زند
  const tick = useRef(0);
  useEffect(() => {
    if (!open || tab !== 'release' || !canManage || !data) return;
    if (cap <= 0) { setPlan(null); return; }
    const my = ++tick.current;
    setPlanning(true);
    const id = setTimeout(async () => {
      try {
        const p = await storeEscrowService.preview(storeId, releaseInput());
        if (my === tick.current) setPlan(p);
      } catch {
        if (my === tick.current) setPlan(null);
      } finally {
        if (my === tick.current) setPlanning(false);
      }
    }, 320);
    return () => { clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, canManage, data, scope, amount, cap, storeId]);

  /** اجرای اقدامی که کاربر در پنجرهٔ تأیید قبول کرده */
  const runConfirmed = async (why: string) => {
    if (!confirm) return;
    const reason = why.trim() || undefined;
    setBusy(true);
    try {
      let msg = '';
      if (confirm.kind === 'release') msg = (await storeEscrowService.release(storeId, { ...confirm.input, reason })).message || 'آزاد شد';
      else if (confirm.kind === 'hold') msg = (await storeEscrowService.hold(storeId, { itemIds: confirm.itemIds, days: confirm.days, reason })).message || 'عقب انداخته شد';
      else msg = (await storeEscrowService.clear(storeId, confirm.itemIds)).message || 'به حالت عادی برگشت';
      toast.success(msg);
      setConfirm(null); setPicked([]); setAmount(0);
      await load(); onChanged?.();
    } catch (e: any) { toast.error(e?.message || 'خطا در انجام این کار'); }
    finally { setBusy(false); }
  };

  /** آزادسازی سفارش‌های مشخص (یک سطر یا انتخاب گروهی): اول پیش‌نمایش، بعد تأیید */
  const askRelease = async (ids: string[]) => {
    if (!ids.length) return;
    const input = { mode: 'items' as const, itemIds: ids, includeAwaitingDelivery: true, includeBlocked: true };
    setBusy(true);
    try {
      const p = await storeEscrowService.preview(storeId, input);
      if (!p.count) { toast.info('این انتخاب چیزی برای آزادکردن ندارد'); return; }
      setConfirm({ kind: 'release', plan: p, input });
    } catch (e: any) { toast.error(e?.message || 'خطا در پیش‌نمایش'); }
    finally { setBusy(false); }
  };

  const askHold = (ids: string[], sum: number) => {
    const d = Number(toEn(holdDays));
    if (!d || d <= 0) { toast.error('چند روز عقب بیفتد؟'); return; }
    if (ids.length) setConfirm({ kind: 'hold', itemIds: ids, amount: sum, days: d });
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const n = (v: string) => (v.trim() === '' ? null : Number(toEn(v)));
      await storeEscrowService.savePolicy(storeId, {
        digitalDays: n(pol.digital), postalDays: n(pol.postal), physicalDays: n(pol.physical),
        note: pol.note.trim() || undefined,
      });
      toast.success('ذخیره شد');
      await load(); onChanged?.();
    } catch (e: any) { toast.error(e?.message || 'خطا در ذخیره'); }
    finally { setSavingPolicy(false); }
  };

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // فیلتر جدول
  const shown = useMemo(() => {
    const needle = toEn(q).trim().toLowerCase();
    return items.filter((i) => {
      if (bucketFilter === 'risky' && !i.releasedEarly) return false;
      if (bucketFilter !== 'all' && bucketFilter !== 'risky' && i.bucket !== bucketFilter) return false;
      if (!needle) return true;
      return `${i.orderNumber || ''} ${i.productTitle || ''}`.toLowerCase().includes(needle);
    });
  }, [items, q, bucketFilter]);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    items.forEach((i) => { c[i.bucket] = (c[i.bucket] || 0) + 1; });
    return c;
  }, [items]);
  const pickedAmount = items.filter((i) => picked.includes(i.orderItemId)).reduce((s, r) => s + r.amount, 0);

  /** اقدام‌های یک سطر */
  const rowActs = (it: EscrowItem) => (
    <div className="esc-row-acts" onClick={(e) => e.stopPropagation()}>
      <Tooltip title="همین حالا آزادش کن" arrow>
        <button className="go" disabled={busy || it.bucket === 'released'} onClick={() => askRelease([it.orderItemId])}>
          <LockOpenRoundedIcon />
        </button>
      </Tooltip>
      <Tooltip title={`آزادشدنش ${fa(holdDays)} روز عقب بیفتد`} arrow>
        <button disabled={busy} onClick={() => askHold([it.orderItemId], it.amount)}>
          <LockClockRoundedIcon />
        </button>
      </Tooltip>
      <Tooltip title={it.overridden ? 'دخالت دستی پاک شود' : 'دخالت دستی ندارد'} arrow>
        <button disabled={busy || !it.overridden} onClick={() => setConfirm({ kind: 'clear', itemIds: [it.orderItemId], amount: it.amount })}>
          <UndoRoundedIcon />
        </button>
      </Tooltip>
    </div>
  );

  /** یک فیلد مدت گارانتی + مثال تاریخ واقعی */
  const polField = (key: 'digital' | 'postal' | 'physical', label: string, hint: string) => {
    const g = data?.policy.global[key === 'digital' ? 'digitalDays' : key === 'postal' ? 'postalDays' : 'physicalDays'];
    const mine = pol[key].trim() === '' ? g : Number(toEn(pol[key]));
    return (
      <div>
        <TextField
          size="small" fullWidth label={label} value={pol[key]} disabled={!canManage}
          onChange={(e) => setPol({ ...pol, [key]: e.target.value })}
          placeholder={`خالی = ${fa(g ?? '')} روز`}
          helperText={pol[key].trim() === '' ? `مثل بقیهٔ فروشگاه‌ها: ${fa(g ?? '')} روز` : `اختصاصی این فروشگاه (سراسری: ${fa(g ?? '')} روز)`}
        />
        <div className="fac-muted" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.75 }}>
          {hint}
          {isFinite(Number(mine)) && <> <br />اگر امروز تحویل شود ⟶ <b>{Number(mine) === 0 ? 'همین امروز' : jdIn(Number(mine))}</b> قابل برداشت.</>}
        </div>
      </div>
    );
  };

  const TABS: { key: 'release' | 'policy' | 'items'; label: string; icon: React.ReactNode }[] = [
    { key: 'release', label: 'آزاد کردن پول', icon: <LockOpenRoundedIcon /> },
    { key: 'policy', label: 'مدت گارانتی', icon: <TuneRoundedIcon /> },
    { key: 'items', label: 'سفارش‌ها', icon: <ReceiptLongIcon /> },
  ];

  return (
    <>
      <ResponsiveModal
        open={open}
        onClose={onClose}
        maxWidth="lg"
        disableClose={busy}
        icon={<LockOpenRoundedIcon />}
        title={`پول فروشنده — ${storeName}`}
        subtitle="آزاد کردن پول قفل‌شده و تنظیم مدت گارانتی همین فروشگاه"
        actions={<Button size="small" onClick={onClose} disabled={busy}>بستن</Button>}
      >
        <div className="fac-page" dir="rtl">
          {loading && !data ? (
            <div className="fac-loading"><CircularProgress /></div>
          ) : !data ? (
            <div className="fac-empty">اطلاعاتی در دسترس نیست</div>
          ) : (
            <>
              {/* ═════════ جوابِ اول: یک جمله، ترکیب پول، خط زمان ═════════ */}
              <div className="esc-top">
                <div className="esc-answer">
                  این فروشنده الان <b>{compact(bal?.availableBalance)} تومان</b> می‌تواند برداشت کند
                  {locked > 0 && <>، و <b className="locked">{compact(locked)} تومان</b> پولش قفل است.</>}
                  {locked === 0 && <> و پول قفل‌شده‌ای ندارد.</>}
                </div>

                {locked > 0 && (
                  <>
                    <div className="esc-mix">
                      <span className="s-escrow" style={{ flexGrow: t?.escrow || 0 }} />
                      <span className="s-await" style={{ flexGrow: t?.awaitingDelivery || 0 }} />
                      <span className="s-blocked" style={{ flexGrow: t?.blocked || 0 }} />
                    </div>
                    <div className="esc-legend">
                      <div><i className="s-escrow" />در گارانتی <b>{compact(t?.escrow)}</b> <small>— کالا تحویل شده</small></div>
                      <div><i className="s-await" />تحویل‌نشده <b>{compact(t?.awaitingDelivery)}</b> <small>— کالا دست خریدار نیست</small></div>
                      <div><i className="s-blocked" />مسدود <b>{compact(t?.blocked)}</b> <small>— اختلاف/بازرسی/پرداخت</small></div>
                      {!!t?.overridden && <div><i style={{ background: 'var(--color-primary)' }} />دستی تغییر داده <b>{compact(t.overridden)}</b></div>}
                    </div>
                  </>
                )}

                {!!t?.releasedEarly && (
                  <div className="esc-risk">
                    <WarningAmberRoundedIcon />
                    <div>
                      <b>{compact(t.releasedEarly)} تومان را دستی آزاد کرده‌ای که کالایش هنوز تحویل نشده یا مانعش برطرف نشده.</b>
                      <span>
                        پول رفته ولی ریسکش روی پلتفرم است: اگر این سفارش‌ها لغو یا مرجوع شوند، باید از فروشنده پس گرفته شود.
                        این اقلام همیشه در تب «سفارش‌ها» دیده می‌شوند تا پیگیری‌شان یادت نرود.
                      </span>
                    </div>
                    <Button size="small" variant="outlined" color="warning"
                      onClick={() => { setTab('items'); setBucketFilter('risky'); }}>
                      ببین کدام‌ها
                    </Button>
                  </div>
                )}

                {when.length > 0 && (
                  <div className="esc-when">
                    <div className="esc-when-l">اگر دست نزنی،<br />خودش آزاد می‌شود:</div>
                    <div className="esc-when-track">
                      {when.map((s, i) => (
                        <div key={s.label} className={`esc-when-step ${i === 0 && s.upTo === 0 ? 'now' : ''}`}>
                          <b>{compact(s.amount)}</b>
                          <span>{s.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ═════════ تب‌ها ═════════ */}
              <div className="esc-tabs">
                {TABS.map((x) => (
                  <button key={x.key} className={tab === x.key ? 'on' : ''} onClick={() => setTab(x.key)}>
                    {x.icon}<em style={{ fontStyle: 'normal' }}>{x.label}</em>
                  </button>
                ))}
              </div>

              {/* ═════════ تب ۱: آزاد کردن پول ═════════ */}
              {tab === 'release' && (!canManage ? (
                <Alert severity="info">برای آزادکردن پول به دسترسی «آزادسازی وجه و مدت گارانتی فروشگاه» نیاز دارید.</Alert>
              ) : locked === 0 ? (
                <div className="fac-card"><div className="esc-none">
                  <DoneAllRoundedIcon />
                  <b>پول قفل‌شده‌ای نیست</b>
                  <span>هر چه داشته آزاد شده. وقتی فروش جدیدی تحویل شود، پولش این‌جا پیدا می‌شود و بعد از مدت گارانتی خودبه‌خود آزاد می‌گردد.</span>
                </div></div>
              ) : (
                <>
                  <div className="esc-step">
                    <div className="esc-step-head">
                      <div className="esc-step-no">۱</div>
                      <b>از کدام پول؟</b>
                      <span>هر چه راست‌تر، پرخطرتر</span>
                    </div>
                    <div className="esc-seg">
                      {SCOPES.map((s) => (
                        <button key={s.key} className={scope === s.key ? 'on' : ''} onClick={() => setScope(s.key)}>
                          <span>{s.short}</span>
                          <b>{compact(ceiling[s.key])}</b>
                          <small>تومان</small>
                        </button>
                      ))}
                    </div>
                    <div className="esc-seg-why">{SCOPES.find((s) => s.key === scope)?.why}</div>
                  </div>

                  <div className="esc-step">
                    <div className="esc-step-head">
                      <div className="esc-step-no">۲</div>
                      <b>چقدرش؟</b>
                      <span>از نزدیک‌ترین سفارش به آزادشدن شروع می‌کنیم</span>
                    </div>
                    <div className="esc-amount">
                      <div className="esc-amount-slider">
                        <Slider
                          value={Math.min(amount || cap, cap)} min={0} max={cap || 1}
                          step={Math.max(1000, Math.round(cap / 200))}
                          onChange={(_, v) => setAmount(Number(v))}
                          valueLabelDisplay="auto" valueLabelFormat={(v) => compact(v)}
                          disabled={cap <= 0}
                        />
                        <div className="esc-ends"><span>۰</span><span>همه‌اش · {compact(cap)}</span></div>
                      </div>
                      <div className="esc-amount-val">
                        <b>{compact(amount > 0 ? amount : cap)}</b>
                        <span>{amount > 0 && amount < cap ? 'تومان (بخشی)' : 'تومان (همه‌اش)'}</span>
                      </div>
                    </div>
                    <div className="fac-quick" style={{ marginTop: 9 }}>
                      {[1e6, 5e6, 1e7, 5e7].filter((v) => v < cap).map((v) => (
                        <button key={v} onClick={() => setAmount(v)}>{compact(v)}</button>
                      ))}
                      <button onClick={() => setAmount(Math.round(cap / 2))}>نصفش</button>
                      <button onClick={() => setAmount(0)}>همه‌اش</button>
                    </div>
                  </div>

                  <div className="esc-step">
                    <div className="esc-step-head">
                      <div className="esc-step-no">۳</div>
                      <b>نتیجه</b>
                      <span>تا دکمهٔ سبز را نزنی هیچ‌چیز عوض نمی‌شود</span>
                    </div>
                    <div className={`esc-result ${plan?.count ? 'go' : ''}`}>
                      <div className="esc-result-txt">
                        {planning ? 'در حال حساب‌کردن…'
                          : plan?.count ? <><b>{fa(plan.count)} سفارش</b> به مبلغ <b>{toman(plan.totalAmount)}</b> همین حالا قابل برداشت می‌شود.</>
                            : 'با این انتخاب چیزی برای آزادکردن نیست.'}
                        {!!plan?.warnings?.length && (
                          <div className="esc-warns">{plan.warnings.map((w, i) => <div key={i}><span>{w}</span></div>)}</div>
                        )}
                      </div>
                      <Button
                        variant="contained" color="success" size="large"
                        disabled={busy || planning || !plan?.count}
                        onClick={() => plan && setConfirm({ kind: 'release', plan, input: releaseInput() })}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LockOpenRoundedIcon />}
                      >
                        آزاد کن
                      </Button>
                    </div>
                    <p className="fac-note" style={{ marginTop: 8 }}>
                      می‌خواهی خودت سفارش‌ها را دانه‌به‌دانه انتخاب کنی؟ تب «سفارش‌ها».
                    </p>
                  </div>
                </>
              ))}

              {/* ═════════ تب ۲: مدت گارانتی ═════════ */}
              {tab === 'policy' && (
                <div className="fac-card">
                  <div className="fac-card-head">
                    <TuneRoundedIcon />
                    <h3>مدت گارانتی این فروشگاه</h3>
                    <div className="fac-actions">
                      {data.policy.custom ? <span className="fac-tag blue">اختصاصی</span> : <span className="fac-tag gray">مثل بقیه</span>}
                      {canManage && (
                        <Button size="small" variant="contained" disabled={savingPolicy} onClick={savePolicy}
                          startIcon={savingPolicy ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}>
                          ذخیره
                        </Button>
                      )}
                    </div>
                    <p>
                      چند روز بعد از تحویل، پول فروشنده خودبه‌خود قابل برداشت شود. فیلد را <b>خالی</b> بگذاری یعنی مثل همهٔ
                      فروشگاه‌ها؛ <b>۰</b> یعنی همان لحظهٔ تحویل.
                      {data.policy.updatedAt ? ` آخرین تغییر: ${jd(data.policy.updatedAt, true)}${data.policy.updatedByName ? ` — ${data.policy.updatedByName}` : ''}` : ''}
                    </p>
                  </div>
                  <div className="fac-card-body">
                    <div className="fac-form">
                      {polField('digital', 'کالای دیجیتال (روز)', 'کد و محصول دیجیتال — از لحظهٔ تحویل آخرین قلم')}
                      {polField('postal', 'کالای پستی (روز)', 'از لحظهٔ رسیدن مرسوله به خریدار')}
                      {polField('physical', 'کالای بازرسی‌شده (روز)', 'از لحظهٔ تحویل بعد از بازرسی')}
                      <TextField className="wide" size="small" label="یادداشت: چرا این فروشگاه فرق دارد؟" value={pol.note}
                        disabled={!canManage} onChange={(e) => setPol({ ...pol, note: e.target.value })} />
                    </div>
                    <div className="esc-eg">
                      این تنظیم روی سفارش‌های <b>آینده و فعلی</b> هر دو اثر می‌گذارد: تاریخ آزادسازی همهٔ سفارش‌های در گارانتی
                      دوباره حساب می‌شود. سفارش‌هایی که دستی آزاد یا عقب انداخته شده‌اند دست‌نخورده می‌مانند.
                    </div>
                    {!canManage && <p className="fac-note">برای تغییر به دسترسی «آزادسازی وجه و مدت گارانتی فروشگاه» نیاز دارید.</p>}
                  </div>
                </div>
              )}

              {/* ═════════ تب ۳: سفارش‌ها ═════════ */}
              {tab === 'items' && (
                <div className="fac-card">
                  <div className="fac-card-head">
                    <ReceiptLongIcon />
                    <h3>سفارش‌های این فروشگاه</h3>
                    <div className="fac-actions">
                      <Button size="small" variant={showReleased ? 'contained' : 'outlined'} onClick={() => setShowReleased(!showReleased)}>
                        {showReleased ? 'پنهان‌کردن آزادشده‌ها' : 'آزادشده‌ها را هم ببین'}
                      </Button>
                      <Button size="small" startIcon={loading ? <CircularProgress size={13} /> : <RefreshIcon />} disabled={loading} onClick={load}>
                        به‌روزرسانی
                      </Button>
                    </div>
                    <p>
                      نزدیک‌ترین آزادسازی اول آمده. {canManage ? 'هر سطر سه دکمهٔ اقدام دارد؛ برای کار گروهی تیک بزن. ' : ''}
                      سفارشی که دستی آزاد کرده‌ای ولی تحویل نشده، همیشه این‌جا می‌ماند تا پیگیری‌اش یادت نرود.
                    </p>
                  </div>
                  <div className="fac-card-body">
                    {items.length === 0 ? (
                      <div className="esc-none">
                        <DoneAllRoundedIcon />
                        <b>سفارشی در گارانتی یا انتظار تحویل نیست</b>
                        <span>یعنی پول همهٔ فروش‌های این فروشگاه تعیین تکلیف شده. با «آزادشده‌ها را هم ببین» می‌توانی تاریخچه را ببینی.</span>
                      </div>
                    ) : (
                      <>
                        <div className="esc-filters">
                          <TextField
                            size="small" value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="شمارهٔ سفارش یا نام محصول…" sx={{ flex: '1 1 220px' }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                          />
                          <div className="esc-chips">
                            <button className={bucketFilter === 'all' ? 'on' : ''} onClick={() => setBucketFilter('all')}>
                              همه <i>{fa(items.length)}</i>
                            </button>
                            {!!t?.releasedEarly && (
                              <button className={bucketFilter === 'risky' ? 'on risky' : 'risky'} onClick={() => setBucketFilter('risky')}>
                                زودهنگام آزاد شده <i>{fa(items.filter((i) => i.releasedEarly).length)}</i>
                              </button>
                            )}
                            {(Object.keys(BUCKET_META) as EscrowBucket[]).filter((b) => counts[b]).map((b) => (
                              <button key={b} className={bucketFilter === b ? 'on' : ''} onClick={() => setBucketFilter(b)}>
                                {BUCKET_META[b].label} <i>{fa(counts[b])}</i>
                              </button>
                            ))}
                          </div>
                        </div>

                        {shown.length === 0 ? (
                          <div className="fac-empty">با این جست‌وجو چیزی پیدا نشد</div>
                        ) : narrow ? (
                          /* ── موبایل: کارت، نه جدولِ اسکرولی ── */
                          <div className="esc-cards">
                            {shown.map((it) => {
                              const meta = BUCKET_META[it.bucket];
                              const sel = picked.includes(it.orderItemId);
                              return (
                                <div key={it.orderItemId} className={`esc-card ${sel ? 'sel' : ''}`}>
                                  <div className="esc-card-top">
                                    {canManage && <Checkbox size="small" sx={{ p: 0 }} checked={sel} onChange={() => toggle(it.orderItemId)} />}
                                    <div className="t">
                                      <b>{it.productTitle}{it.quantity > 1 ? ` ×${fa(it.quantity)}` : ''}</b>
                                      <span>{it.orderNumber}</span>
                                    </div>
                                    <div className="esc-card-amt">{toman(it.amount)}</div>
                                  </div>
                                  <div className="esc-card-meta">
                                    <span>تحویل: <b>{jd(it.deliveredAt)}</b></span>
                                    <span>آزاد می‌شود: <b>{jd(it.releaseDate)}</b>{it.daysLeft != null && it.daysLeft > 0 ? ` (${fa(it.daysLeft)} روز)` : ''}</span>
                                    <span>{it.inspected ? 'بازرسی‌شده' : KIND_FA[it.fulfillmentType || ''] || 'دیجیتال'} · گارانتی {fa(it.warrantyDays)} روز</span>
                                  </div>
                                  <div className="esc-card-foot">
                                    {it.releasedEarly
                                      ? <Tooltip title={it.riskNote || ''} arrow><span className="fac-tag purple">زودهنگام آزاد شده</span></Tooltip>
                                      : <Tooltip title={it.blockedReason || meta.hint} arrow><span className={`fac-tag ${meta.color}`}>{meta.label}</span></Tooltip>}
                                    {it.overridden && !it.releasedEarly && <span className="fac-tag purple">{it.override?.kind === 'hold' ? 'دستی عقب افتاده' : 'دستی آزاد شده'}</span>}
                                    {canManage && rowActs(it)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="fac-table-wrap">
                            <table className="fac-table" style={{ minWidth: 880 }}>
                              <thead>
                                <tr>
                                  {canManage && (
                                    <th style={{ width: 40 }}>
                                      <Checkbox size="small"
                                        checked={shown.length > 0 && shown.every((i) => picked.includes(i.orderItemId))}
                                        indeterminate={shown.some((i) => picked.includes(i.orderItemId)) && !shown.every((i) => picked.includes(i.orderItemId))}
                                        onChange={() => {
                                          const ids = shown.map((i) => i.orderItemId);
                                          const all = ids.every((id) => picked.includes(id));
                                          setPicked(all ? picked.filter((id) => !ids.includes(id)) : Array.from(new Set([...picked, ...ids])));
                                        }} />
                                    </th>
                                  )}
                                  <th>سفارش</th><th>مبلغ</th><th>تحویل</th><th>آزاد می‌شود</th><th>وضعیت</th>
                                  {canManage && <th style={{ width: 104 }}>اقدام</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {shown.map((it) => {
                                  const meta = BUCKET_META[it.bucket];
                                  return (
                                    <tr key={it.orderItemId} className={canManage ? 'clickable' : ''}
                                      onClick={canManage ? () => toggle(it.orderItemId) : undefined}
                                      style={{ opacity: it.bucket === 'released' ? 0.62 : 1 }}>
                                      {canManage && (
                                        <td><Checkbox size="small" checked={picked.includes(it.orderItemId)}
                                          onChange={() => toggle(it.orderItemId)} onClick={(e) => e.stopPropagation()} /></td>
                                      )}
                                      <td style={{ maxWidth: 250 }}>
                                        {it.productTitle}{it.quantity > 1 ? ` ×${fa(it.quantity)}` : ''}
                                        <div className="fac-muted" style={{ fontSize: 10, fontFamily: 'monospace', direction: 'ltr', textAlign: 'right' }}>{it.orderNumber}</div>
                                      </td>
                                      <td className="fac-num" style={{ fontWeight: 700 }}>{toman(it.amount)}</td>
                                      <td className="fac-num">{jd(it.deliveredAt)}</td>
                                      <td className="fac-num">
                                        {jd(it.releaseDate)}
                                        {it.daysLeft != null && it.daysLeft > 0 && <div className="fac-muted" style={{ fontSize: 10 }}>{fa(it.daysLeft)} روز دیگر</div>}
                                        {it.overridden && (
                                          <div style={{ fontSize: 10, color: 'var(--color-primary)' }}>
                                            {it.override?.kind === 'hold' ? 'دستی عقب افتاده' : 'دستی آزاد شده'}
                                            {it.override?.byName ? ` — ${it.override.byName}` : ''}
                                          </div>
                                        )}
                                      </td>
                                      <td>
                                        {it.releasedEarly ? (
                                          <Tooltip title={it.riskNote || ''} arrow>
                                            <span className="fac-tag purple">زودهنگام آزاد شده</span>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title={it.blockedReason || meta.hint} arrow>
                                            <span className={`fac-tag ${meta.color}`}>{meta.label}</span>
                                          </Tooltip>
                                        )}
                                        {it.releasedEarly && !it.deliveredAt && (
                                          <div className="fac-muted" style={{ fontSize: 10, marginTop: 2 }}>کالا تحویل نشده</div>
                                        )}
                                      </td>
                                      {canManage && <td>{rowActs(it)}</td>}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {items.length >= 800 && <Alert severity="info" sx={{ mt: 1 }}>فقط ۸۰۰ سفارش آخر نمایش داده می‌شود.</Alert>}

                        {/* نوار اقدام گروهی — فقط وقتی چیزی انتخاب شده */}
                        {canManage && picked.length > 0 && (
                          <div className="esc-bar">
                            <div className="esc-bar-sum">
                              {fa(picked.length)} سفارش انتخاب شد
                              <span>{toman(pickedAmount)}</span>
                            </div>
                            <span className="esc-bar-sp" />
                            <Button size="small" variant="contained" color="success" disabled={busy}
                              startIcon={<LockOpenRoundedIcon />} onClick={() => askRelease(picked)}>
                              همین حالا آزاد کن
                            </Button>
                            <TextField size="small" label="روز" value={holdDays} onChange={(e) => setHoldDays(e.target.value)} sx={{ width: 84 }} />
                            <Button size="small" variant="outlined" color="warning" disabled={busy}
                              startIcon={<LockClockRoundedIcon />} onClick={() => askHold(picked, pickedAmount)}>
                              عقب بنداز
                            </Button>
                            <Button size="small" variant="outlined" disabled={busy} startIcon={<UndoRoundedIcon />}
                              onClick={() => setConfirm({ kind: 'clear', itemIds: picked, amount: pickedAmount })}>
                              برگردان به عادی
                            </Button>
                            <Button size="small" disabled={busy} onClick={() => setPicked([])}>لغو انتخاب</Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </ResponsiveModal>

      {/* ───── تأیید نهایی — تنها جایی که چیزی واقعاً عوض می‌شود ───── */}
      <ConfirmAsk ask={confirm} busy={busy} onCancel={() => setConfirm(null)} onYes={runConfirmed} />
    </>
  );
};

/**
 * پنجرهٔ تأیید — برای هر سه اقدام یکی است: به زبان ساده می‌گوید چه می‌شود،
 * هشدارها را نشان می‌دهد و دلیل را همین‌جا می‌گیرد تا در دفتر فروشنده ثبت شود.
 */
const ConfirmAsk: React.FC<{
  ask: ConfirmAskShape | null;
  busy: boolean;
  onCancel: () => void;
  onYes: (reason: string) => void;
}> = ({ ask, busy, onCancel, onYes }) => {
  const [why, setWhy] = useState('');
  useEffect(() => { if (ask) setWhy(''); }, [ask]);
  if (!ask) return null;

  const copy = ask.kind === 'release'
    ? {
      title: 'این پول را آزاد کنم؟',
      line: <><b>{fa(ask.plan.count)} سفارش</b> به مبلغ <b>{toman(ask.plan.totalAmount)}</b> همین حالا به موجودی قابل‌برداشت فروشنده اضافه می‌شود و می‌تواند درخواست برداشت بدهد.</>,
      yes: 'بله، آزاد کن', color: 'success' as const, tone: 'go',
      icon: <LockOpenRoundedIcon />, warnings: ask.plan.warnings, rows: ask.plan.rows,
    }
    : ask.kind === 'hold'
      ? {
        title: 'آزادشدن را عقب بیندازم؟',
        line: <><b>{fa(ask.itemIds.length)} سفارش</b> به مبلغ <b>{toman(ask.amount)}</b> تا <b>{fa(ask.days)} روز دیگر</b> ({jdIn(ask.days)}) قابل برداشت نمی‌شود. اگر قبلاً آزاد شده بود، از موجودی قابل‌برداشت کم می‌شود.</>,
        yes: 'بله، عقب بینداز', color: 'warning' as const, tone: '',
        icon: <LockClockRoundedIcon />, warnings: [] as string[], rows: [] as EscrowItem[],
      }
      : {
        title: 'برگردد به حالت عادی؟',
        line: <>دخالت دستی روی <b>{fa(ask.itemIds.length)} سفارش</b> ({toman(ask.amount)}) پاک می‌شود و تاریخ آزادشدن دوباره از قاعدهٔ «تحویل + مدت گارانتی» حساب می‌شود.</>,
        yes: 'بله، برگردان', color: 'primary' as const, tone: '',
        icon: <UndoRoundedIcon />, warnings: [] as string[], rows: [] as EscrowItem[],
      };

  return (
    <Dialog open onClose={() => !busy && onCancel()} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>{copy.title}</DialogTitle>
      <DialogContent>
        <div className="fac-page" style={{ padding: 0 }}>
          <div className={`esc-result ${copy.tone}`}>
            <div className="esc-result-txt">
              {copy.line}
              {!!copy.warnings.length && (
                <div className="esc-warns">{copy.warnings.map((w, i) => <div key={i}><span>{w}</span></div>)}</div>
              )}
            </div>
          </div>

          {copy.rows.length > 0 && (
            <div className="fac-table-wrap" style={{ marginTop: 10 }}>
              <table className="fac-table">
                <thead><tr><th>سفارش</th><th>محصول</th><th>مبلغ</th></tr></thead>
                <tbody>
                  {copy.rows.slice(0, 8).map((r) => (
                    <tr key={r.orderItemId}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, direction: 'ltr', textAlign: 'right' }}>{r.orderNumber}</td>
                      <td>{r.productTitle}</td>
                      <td className="fac-num">{toman(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {copy.rows.length > 8 && <p className="fac-note">و {fa(copy.rows.length - 8)} سفارش دیگر…</p>}
            </div>
          )}

          <TextField
            sx={{ mt: 1.5 }} size="small" fullWidth value={why} onChange={(e) => setWhy(e.target.value)}
            label="دلیل (اختیاری)" placeholder="مثلاً: فروشندهٔ قدیمی و مورد اعتماد"
            helperText="همین متن در دفتر رویدادهای مالی فروشنده کنار نام خودت ثبت می‌شود"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onCancel} disabled={busy}>نه، بی‌خیال</Button>
        <Button size="small" variant="contained" color={copy.color} disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : copy.icon}
          onClick={() => onYes(why)}>
          {copy.yes}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreEscrowDialog;
